<template>
  <section class="agreement-ip-block" aria-labelledby="agreement-ip-block-title">
    <div class="page-heading">
      <div>
        <h2 id="agreement-ip-block-title">签约ip屏蔽</h2>
        <p>按请求 IP 的归属省市限制签约下单。</p>
      </div>
      <a-button :loading="loading" :disabled="saving || dirty" @click="loadConfig">刷新</a-button>
    </div>
    <a-alert class="rule-description" type="info" show-icon message="省份下可多选城市，留空表示屏蔽整省；北京、天津、上海、重庆按整市屏蔽。删除全部地区并保存后，取消地区屏蔽。" />
    <a-alert v-if="loadError" class="load-error" type="error" show-icon :message="loadError" />
    <a-spin :spinning="loading">
      <a-card class="config-card" :bordered="false" title="屏蔽地区">
        <template #extra>
          <a-button :disabled="!loaded || loading || saving" @click="addRow">添加省份</a-button>
        </template>
        <a-empty v-if="loaded && !rows.length" description="尚未配置屏蔽地区" />
        <div v-for="(row, index) in rows" :key="row.id" class="region-row">
          <div class="region-field province-field">
            <label :for="`province-${row.id}`">省份</label>
            <a-select :id="`province-${row.id}`" v-model:value="row.province" show-search option-filter-prop="label"
              :options="provinceOptions(row)" :disabled="!loaded || loading || saving" placeholder="请选择省份"
              :aria-label="`第 ${index + 1} 行省份`" @change="changeProvince(row)" />
          </div>
          <div class="region-field city-field">
            <label :for="`cities-${row.id}`">城市</label>
            <a-select :id="`cities-${row.id}`" v-model:value="row.cities" mode="multiple" allow-clear show-search
              option-filter-prop="label" :options="cityOptions(row.province)"
              :disabled="!loaded || loading || saving || !row.province || isDirectMunicipality(row.province)"
              :placeholder="isDirectMunicipality(row.province) ? '直辖市按整市屏蔽' : '留空表示屏蔽整省，可多选城市'"
              :aria-label="`第 ${index + 1} 行城市，留空屏蔽整省`" />
          </div>
          <a-button danger :disabled="!loaded || loading || saving" :aria-label="`删除第 ${index + 1} 行地区`" @click="removeRow(row.id)">删除</a-button>
        </div>
        <a-alert v-if="validationError" class="validation-error" type="warning" show-icon :message="validationError" />
        <div class="save-bar">
          <span class="save-hint">{{ dirty ? '配置尚未保存' : loaded ? '当前配置已保存' : '加载完成后可编辑' }}</span>
          <a-space>
            <a-button :disabled="!dirty || loading || saving" @click="resetChanges">撤销修改</a-button>
            <a-button type="primary" :loading="saving" :disabled="!canSave" @click="saveConfig">保存配置</a-button>
          </a-space>
        </div>
      </a-card>
    </a-spin>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import { get, postJson } from "@/utils/request";
import { analyticsUrl } from "@/utils/api";
import { isDirectMunicipality, readRegionDirectory, regionsToRows, rowsToRegions } from "@/utils/agreementIpRegions";

const API_PATH = "/operate/agreement-ip-block";
const rows = ref([]);
const savedRows = ref([]);
const directory = ref([]);
const loaded = ref(false);
const loading = ref(false);
const saving = ref(false);
const loadError = ref("");
let nextId = 1;
const editableRows = (items) => items.map((row) => ({ province: row.province, cities: [...row.cities] }));
const dirty = computed(() => JSON.stringify(editableRows(rows.value)) !== JSON.stringify(savedRows.value));
const validationError = computed(() => {
  try { rowsToRegions(rows.value, directory.value); return ""; }
  catch (error) { return error.message; }
});
const canSave = computed(() => loaded.value && !loading.value && !saving.value && dirty.value && !validationError.value);
const provinceOptions = (row) => directory.value.map((item) => ({
  label: item.name, value: item.name,
  disabled: item.name !== row.province && rows.value.some((other) => other.province === item.name),
}));
const cityOptions = (province) => (directory.value.find((item) => item.name === province)?.cities || [])
  .map((city) => ({ label: city, value: city }));
const changeProvince = (row) => { row.cities = []; };
const addRow = () => {
  if (!loaded.value || loading.value || saving.value) return;
  rows.value.push({ id: nextId++, province: undefined, cities: [] });
};
const removeRow = (id) => {
  if (loading.value || saving.value) return;
  rows.value = rows.value.filter((row) => row.id !== id);
};
const resetChanges = () => {
  if (loading.value || saving.value) return;
  rows.value = savedRows.value.map((row) => ({ ...row, cities: [...row.cities], id: nextId++ }));
};
const applyConfig = (data) => {
  const normalized = regionsToRows(data?.regions, directory.value);
  savedRows.value = editableRows(normalized);
  rows.value = normalized.map((row) => ({ ...row, id: nextId++ }));
  loaded.value = true;
};

/** 两个读取都成功才允许编辑；刷新不会覆盖未保存草稿。 */
const loadConfig = async () => {
  if (loading.value || saving.value || dirty.value) return;
  loading.value = true;
  loaded.value = false;
  loadError.value = "";
  try {
    const [regionResponse, configResponse] = await Promise.all([
      get(analyticsUrl(`${API_PATH}/regions`)), get(analyticsUrl(`${API_PATH}/config`)),
    ]);
    if (regionResponse.code !== 200) throw new Error(regionResponse.content || "加载省市目录失败");
    if (configResponse.code !== 200) throw new Error(configResponse.content || "加载屏蔽配置失败");
    directory.value = readRegionDirectory(regionResponse.data);
    applyConfig(configResponse.data);
  } catch (error) {
    loadError.value = error.message || error.content || "加载屏蔽配置失败";
  } finally { loading.value = false; }
};

/** 提交完整省市集合，失败保留草稿，成功用后端规范化结果回填。 */
const saveConfig = async () => {
  if (!canSave.value) return;
  saving.value = true;
  try {
    const response = await postJson(analyticsUrl(`${API_PATH}/config/update`), {
      regions: rowsToRegions(rows.value, directory.value),
    });
    if (response.code !== 200) throw new Error(response.content || "保存屏蔽配置失败");
    applyConfig(response.data);
    message.success("签约 IP 屏蔽配置已保存");
  } catch (error) {
    message.error(error.message || error.content || "保存屏蔽配置失败");
  } finally { saving.value = false; }
};
onMounted(loadConfig);
</script>

<style scoped>
.page-heading { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 20px; }
.page-heading h2 { margin: 0 0 6px; font-size: 20px; color: #172b4d; }
.page-heading p, .save-hint { margin: 0; color: #65758b; }
.rule-description, .load-error { margin-bottom: 16px; }
.config-card { border-radius: 12px; }
.region-row { display: flex; align-items: flex-end; gap: 16px; margin-bottom: 16px; }
.region-field { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.province-field { width: 220px; }
.city-field { flex: 1; }
.region-field label { color: #4b5d75; }
.validation-error { margin-top: 16px; }
.save-bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 24px; }
@media (max-width: 768px) {
  .region-row { flex-wrap: wrap; gap: 12px; padding-bottom: 16px; border-bottom: 1px solid #edf0f5; }
  .province-field, .city-field { flex: 0 0 100%; width: 100%; }
  .region-row > .ant-btn { margin-left: auto; }
  .save-bar { align-items: flex-start; flex-direction: column; }
  .save-bar :deep(.ant-space) { align-self: flex-end; }
}
</style>
